import { TextField } from '@mui/material'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import UtilitiesService from '../../services/UtilitiesService'
import CustomAutocomplete from '../../components/Autocomplete'
import CustomSwitch from '../../components/Switch'
import { normalizeString } from '../../helpers/utils'
import CustomCheckbox from '../../components/Checkbox'

interface Props {
    value: string
    setValue: (jobTitle: string) => void
    checked: { freelance: boolean; entrepreneur: boolean }
    setChecked: Dispatch<SetStateAction<{ freelance: boolean; entrepreneur: boolean }>>
}
const CompanyFinder = ({ value, setValue, checked, setChecked }: Props) => {
    const [companies, setCompanies] = useState<string[] | null>()
    const [filter, setFilter] = useState<string>('')
    const [startsWith, setStartsWith] = useState<boolean>(false)

    useEffect(() => {
        if (!filter) {
            setCompanies(undefined)
            return
        }

        setCompanies(null)
        const timeout = setTimeout(() => {
            new UtilitiesService().getCompanies(filter, startsWith).then((response) => {
                if (response.status !== 200) return
                setCompanies(response.data)
            })
        }, 1500)

        return () => clearTimeout(timeout)
    }, [filter, startsWith])

    const handleChecked = (name: string) => {
        setValue('')
        if (name === 'freelancer') {
            setChecked((prev) => ({
                entrepreneur: prev.entrepreneur ? false : prev.entrepreneur,
                freelance: prev.freelance ? false : true,
            }))
        } else {
            setChecked((prev) => ({
                entrepreneur: prev.entrepreneur ? false : true,
                freelance: prev.freelance ? false : prev.freelance,
            }))
        }
    }

    return (
        <div className="w-full flex flex-col md:flex-row gap-1 md:gap-2 items-center">
            <CustomAutocomplete
                value={value}
                disabled={checked.entrepreneur || checked.freelance}
                inputValue={filter}
                options={
                    companies
                        ? companies.length > 0
                            ? companies
                            : [filter]
                        : !!filter
                          ? ['Buscando...']
                          : ['Comenzá a buscar, o podés agregarla si no se encuentra.']
                }
                filterOptions={(options) => {
                    return options.filter(
                        (x) =>
                            (startsWith
                                ? normalizeString(x.toLowerCase()).startsWith(
                                      normalizeString(filter.toLowerCase()),
                                  )
                                : normalizeString(x.toLowerCase()).includes(
                                      normalizeString(filter.toLowerCase()),
                                  )) || x === filter,
                    )
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Empresa"
                    />
                )}
                getOptionDisabled={(option) =>
                    option === 'Buscando...' ||
                    option === 'Comenzá a buscar, o podés agregarla si no se encuentra.'
                }
                renderOption={(props, option) => {
                    const { ...optionProps } = props
                    return (
                        <li
                            key={option}
                            {...optionProps}
                        >
                            {option === filter && companies!.length === 0
                                ? `Agregar "${filter}"`
                                : option}
                        </li>
                    )
                }}
                onInputChange={(e, input) => setFilter(input)}
                onChange={(e, company) => setValue(company ?? '')}
                noOptionsText={companies ? 'No hay resultados' : 'Buscando...'}
                className="md:mr-2 w-full"
                selectOnFocus
            />
            <div className="flex flex-row md:flex-col gap-1 md:gap-2 items-center">
                <div className="flex flex-col md:flex-row gap-1 md:gap-2 items-center">
                    <CustomSwitch
                        value={startsWith}
                        onChange={(e) => setStartsWith(e.target.checked)}
                        disabled={checked.entrepreneur || checked.freelance}
                    />
                    <span className="whitespace-nowrap text-[#777777] ml-2 md:ml-0 md:mr-1 text-xs md:text-base">
                        Búsqueda textual
                    </span>
                </div>
                <div className="flex flex-col md:flex-row gap-2 md:gap-4 items-end md:items-start">
                    <div className="flex gap-1 items-center">
                        <span className="text-[#777777] text-xs md:text-base">Emprendedor</span>
                        <CustomCheckbox
                            className="p-0 text-[#777777]"
                            name="entrepreneur"
                            checked={checked.entrepreneur}
                            onChange={(e) => handleChecked(e.target.name)}
                        />
                    </div>
                    <div className="flex gap-1 items-center">
                        <span className="text-[#777777] text-xs md:text-base">Freelancer</span>
                        <CustomCheckbox
                            className="p-0 text-[#777777]"
                            name="freelancer"
                            checked={checked.freelance}
                            onChange={(e) => handleChecked(e.target.name)}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}

export default CompanyFinder
